import { useRef } from "react";
import { motion, useScroll, useTransform, useSpring, useMotionValue } from "framer-motion";
import { ArrowRight, Sparkles, ShieldCheck, Users } from "lucide-react";
import { LogoMark } from "@/components/relyn/Nav";
import { scrollToSection } from "@/lib/scrollToSection";

const EASE = [0.22, 1, 0.36, 1];

// Headline split into lines for the signature masked reveal.
const LINE1 = "Every Stronger Relationship";
const LINE2A = "Starts With ";
const LINE2B = "Understanding Yourself.";

function MaskLine({ children, delay = 0 }) {
  return (
    <span className="block overflow-hidden pb-[0.08em]">
      <motion.span
        className="block"
        initial={{ y: "110%" }}
        animate={{ y: "0%" }}
        transition={{ duration: 1, delay, ease: EASE }}
      >
        {children}
      </motion.span>
    </span>
  );
}

export default function Hero() {
  const sectionRef = useRef(null);

  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start start", "end start"],
  });
  const phoneYRaw = useTransform(scrollYProgress, [0, 1], [0, -120]);
  const phoneScaleRaw = useTransform(scrollYProgress, [0, 1], [1, 1.08]);
  const phoneY = useSpring(phoneYRaw, { stiffness: 90, damping: 20 });
  const phoneScale = useSpring(phoneScaleRaw, { stiffness: 90, damping: 20 });
  const glowOpacity = useTransform(scrollYProgress, [0, 1], [1, 0.3]);

  // Mouse tilt via motion values (no React re-render per move).
  const tiltXRaw = useMotionValue(0);
  const tiltYRaw = useMotionValue(0);
  const tiltX = useSpring(tiltXRaw, { stiffness: 150, damping: 18 });
  const tiltY = useSpring(tiltYRaw, { stiffness: 150, damping: 18 });

  const onMouseMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    tiltXRaw.set(((e.clientX - r.left) / r.width - 0.5) * 10);
    tiltYRaw.set(((e.clientY - r.top) / r.height - 0.5) * -8);
  };
  const onMouseLeave = () => {
    tiltXRaw.set(0);
    tiltYRaw.set(0);
  };

  return (
    <section
      id="top"
      ref={sectionRef}
      className="relative pt-24 sm:pt-28 md:pt-36 pb-16 md:pb-28 overflow-hidden"
      data-testid="hero-section"
    >
      <div className="relyn-aurora" />
      <div className="absolute inset-0 grid-noise opacity-60" />

      <div className="relative max-w-7xl mx-auto px-6 md:px-10 grid lg:grid-cols-[1.02fr_1.1fr] gap-10 lg:gap-8 items-center">
        {/* Left */}
        <div className="order-1">
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="flex items-center gap-3"
            data-testid="hero-logo-lockup"
          >
            <LogoMark size={64} />
            <span className="text-white text-3xl md:text-4xl tracking-tight" style={{ fontFamily: "Outfit", fontWeight: 600 }}>
              Relyn
            </span>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="mt-6 inline-flex items-center gap-2 px-4 py-1.5 rounded-full glass-soft text-[11px] md:text-xs uppercase tracking-[0.22em] text-[#C0C7D1]"
            data-testid="hero-eyebrow"
          >
            <Sparkles className="w-3.5 h-3.5 text-[#2D8CFF]" />
            The Personal Growth App for Men
          </motion.div>

          <h1
            className="mt-5 text-4xl sm:text-5xl md:text-6xl lg:text-[70px] font-light leading-[1.04] tracking-tight text-white"
            data-testid="hero-headline"
          >
            <MaskLine delay={0.15}>{LINE1}</MaskLine>
            <MaskLine delay={0.28}>
              <span>{LINE2A}</span>
              <span className="bg-gradient-to-r from-[#2D8CFF] via-[#5aa8ff] to-[#C0C7D1] bg-clip-text text-transparent">
                {LINE2B}
              </span>
            </MaskLine>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.5 }}
            className="mt-5 sm:mt-6 text-base sm:text-lg md:text-xl text-[#C0C7D1] leading-relaxed max-w-xl"
            data-testid="hero-subheadline"
          >
            Relyn helps men process life, strengthen relationships, build
            self-awareness, and grow through guidance from Leo, your personal
            growth companion.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.6 }}
            className="mt-7 flex flex-col sm:flex-row gap-3 sm:items-center"
          >
            <button
              type="button"
              onClick={() => scrollToSection("waitlist", { focus: "input" })}
              className="btn-primary inline-flex items-center justify-center gap-2 tap-target"
              data-testid="hero-join-waitlist-btn"
            >
              Join the Waitlist <ArrowRight className="w-4 h-4" />
            </button>
            <button
              type="button"
              onClick={() => scrollToSection("how-it-works")}
              className="btn-ghost inline-flex items-center justify-center gap-2 tap-target"
              data-testid="hero-secondary-cta-btn"
            >
              See How It Works
            </button>
          </motion.div>

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.7, delay: 0.72 }}
            className="mt-5 text-sm text-[#C0C7D1] max-w-xl leading-relaxed"
            data-testid="hero-trust-statement"
          >
            Built for men seeking growth, stronger relationships, and greater
            self-awareness.
          </motion.p>

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.7, delay: 0.8 }}
            className="mt-3 text-[11px] md:text-xs uppercase tracking-[0.22em] text-[#8A99B0]"
            data-testid="hero-credibility"
          >
            Built by an educator, author, entrepreneur, father, and community mentor.
          </motion.p>
        </div>

        {/* Right: kinetic 3-phone composite */}
        <div className="order-2 relative flex justify-center lg:justify-end">
          <motion.div
            style={{ y: phoneY }}
            className="relative w-full"
            onMouseMove={onMouseMove}
            onMouseLeave={onMouseLeave}
          >
            {/* Spotlight */}
              <div
                className="hero-spotlight absolute -inset-x-10 -inset-y-16 blur-2xl pointer-events-none"
                aria-hidden="true"
              />

            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 40 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              transition={{ duration: 1.2, delay: 0.35, ease: EASE }}
              style={{
                scale: phoneScale,
                rotateY: tiltX,
                rotateX: tiltY,
                transformPerspective: 1200,
              }}
              className="relative will-change-transform"
              data-testid="hero-mockup-primary"
            >
              <picture>
                <source
                  media="(max-width: 767px)"
                  type="image/webp"
                  srcSet="/app/hero-trio@mobile.webp"
                />
                <img
                  src="/app/hero-trio.webp"
                  srcSet="/app/hero-trio.webp 1400w, /app/hero-trio@2x.webp 2000w"
                  sizes="(min-width: 1024px) 640px, 92vw"
                  width="1400"
                  height="919"
                  alt="Relyn app — Home Dashboard with daily mood check-in, Leo AI coach, and progress tracking"
                  className="relative w-full h-auto select-none"
                  draggable="false"
                  fetchPriority="high"
                  decoding="async"
                />
              </picture>
            </motion.div>

            {/* Floating live-stat chip */}
            <motion.div
              initial={{ opacity: 0, x: 30, y: 20 }}
              animate={{ opacity: 1, x: 0, y: 0 }}
              transition={{ duration: 0.9, delay: 1, ease: EASE }}
              className="hidden md:flex absolute right-0 top-6 glass rounded-2xl px-4 py-3 items-center gap-3 float-slow"
              data-testid="hero-floating-insight"
            >
              <div className="w-9 h-9 rounded-full bg-gradient-to-br from-[#2D8CFF] to-[#1a5fb0] flex items-center justify-center text-white text-xs font-semibold">
                8.3
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-[0.2em] text-[#C0C7D1]">Relationship</p>
                <p className="text-white text-sm font-medium" style={{ fontFamily: "Outfit" }}>+1.3 this week</p>
              </div>
            </motion.div>
          </motion.div>
        </div>

        {/* Trust row — below phone on mobile, spans on desktop */}
        <div className="order-3 lg:col-span-2 lg:-mt-8">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.9 }}
            className="mt-6 lg:mt-0 flex flex-wrap items-center gap-4 md:gap-5 text-xs text-[#8A99B0]"
            data-testid="hero-trust-row"
          >
            <div className="flex -space-x-2">
              {["JM", "TR", "CK", "DA"].map((i, idx) => (
                <div
                  key={i}
                  className="w-8 h-8 rounded-full border-2 border-[#0A1A2F] flex items-center justify-center text-[10px] font-semibold text-white"
                  style={{ background: ["#2D8CFF", "#1a5fb0", "#3a6da3", "#1f4980"][idx] }}
                >
                  {i}
                </div>
              ))}
            </div>
            <span className="text-[#C0C7D1]" data-testid="hero-community-label">
              Join the founding members of Relyn
            </span>
            <span className="hidden sm:inline-flex items-center gap-1.5">
              <ShieldCheck className="w-3.5 h-3.5 text-[#2D8CFF]" /> Private by default
            </span>
            <span className="hidden md:inline-flex items-center gap-1.5">
              <Users className="w-3.5 h-3.5 text-[#2D8CFF]" /> Built for men
            </span>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
